/// <reference types="@rsbuild/core/types" />
import "@/index.css";
import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext } from "@tanstack/react-router";

import { getMessagesForRoute } from "@/i18n/get-messages-for-route";
import { resolveRouteDisplayLocale } from "@/i18n/resolve-route-display-locale";
import { routeLocaleFromPathname } from "@/i18n/route-locale";
import { distributionAvailabilityQueryOptions } from "@/lib/distribution-availability-query";
import { getViewerContextForRoute } from "@/lib/viewer-context";
import { getRootLoaderData } from "@/types/intl";

import { buildRootHead } from "./-buildRootHead";
import { RootApp } from "./-RootApp";
import { NotFound } from "./not-found";

interface RootContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RootContext>()({
  // i18n lives on route `context` (not `loaderData`) so child routes'
  // `head({ match })` handlers can read it. TanStack's head handler receives
  // a pre-load snapshot of `matches` whose `loaderData` is never populated —
  // only `match.context` (merged from all ancestor `beforeLoad`s via
  // `buildMatchContext`) is available synchronously at head-execution time.
  beforeLoad: async ({ context: { queryClient }, location }) => {
    const viewer = await getViewerContextForRoute(queryClient);
    const routeLocale = routeLocaleFromPathname(location.pathname);
    const locale = resolveRouteDisplayLocale({
      pathname: location.pathname,
      preferredLocale: viewer.preferences.locale,
      routeLocale,
      isMember: viewer.isMember,
    });
    const [messages] = await Promise.all([
      getMessagesForRoute(locale),
      queryClient.ensureQueryData(
        distributionAvailabilityQueryOptions(viewer.userId, viewer.identity),
      ),
    ]);

    return {
      viewer,
      analyticsPathname: location.pathname,
      user: viewer.user,
      viewerLocale: viewer.preferences.locale,
      routeLocale,
      locale,
      messages,
      timeZone: viewer.preferences.timeZone,
      now: viewer.now,
    };
  },
  head: ({ match }) => {
    const { locale, messages } = getRootLoaderData(match);
    return buildRootHead({ locale, messages });
  },
  notFoundComponent: NotFound,
  component: RootComponent,
});

function RootComponent() {
  const {
    queryClient,
    locale,
    messages,
    routeLocale,
    user,
    viewerLocale,
    timeZone: initialTimeZone,
    now: initialNow,
  } = Route.useRouteContext();

  return (
    <RootApp
      initialNow={initialNow}
      initialTimeZone={initialTimeZone}
      locale={locale}
      messages={messages}
      queryClient={queryClient}
      routeLocale={routeLocale}
      user={user}
      viewerLocale={viewerLocale}
    />
  );
}
